/**
 * Maestro sound effects — synthesised via Web Audio API so no assets to ship.
 *
 * Public API:
 *   const sfx = useMaestroSfx();  // React hook
 *   sfx.play("questionStart");
 *   sfx.toggleMute();
 *   sfx.muted // boolean
 *
 * Or as a plain singleton:
 *   maestroSfx.play("timerTick");
 */
import { useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "maestro_sfx_muted";

class SfxEngine {
  constructor() {
    this._ctx = null;
    this._muted = typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1";
  }

  get muted() { return this._muted; }

  setMuted(v) {
    this._muted = !!v;
    try { localStorage.setItem(STORAGE_KEY, this._muted ? "1" : "0"); } catch (e) {}
  }

  toggleMute() { this.setMuted(!this._muted); return this._muted; }

  _ac() {
    if (this._muted) return null;
    if (!this._ctx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      this._ctx = new Ctx();
    }
    // Some browsers keep the context suspended until user gesture.
    if (this._ctx.state === "suspended") { try { this._ctx.resume(); } catch (e) {} }
    return this._ctx;
  }

  // Simple beep helper.
  _beep({ freq = 660, dur = 0.14, type = "sine", vol = 0.18, delay = 0, slideTo = null }) {
    const ctx = this._ac();
    if (!ctx) return;
    const t0 = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(vol, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g); g.connect(ctx.destination);
    osc.start(t0); osc.stop(t0 + dur + 0.02);
  }

  play(name) {
    switch (name) {
      case "questionStart":
        // Cheerful ascending trio.
        this._beep({ freq: 440, slideTo: 660, dur: 0.12, type: "triangle", vol: 0.22 });
        this._beep({ freq: 660, dur: 0.10, delay: 0.14, type: "triangle", vol: 0.22 });
        this._beep({ freq: 880, dur: 0.14, delay: 0.28, type: "triangle", vol: 0.22 });
        break;
      case "timerTick":
        this._beep({ freq: 880, dur: 0.06, type: "square", vol: 0.14 });
        break;
      case "timerLast":
        // Urgency bleep for the very last 3s.
        this._beep({ freq: 1200, dur: 0.09, type: "square", vol: 0.22 });
        break;
      case "reveal":
        // Chord-ish fanfare.
        this._beep({ freq: 523, dur: 0.22, type: "sawtooth", vol: 0.15 });
        this._beep({ freq: 659, dur: 0.22, type: "sawtooth", vol: 0.15, delay: 0.02 });
        this._beep({ freq: 784, dur: 0.30, type: "sawtooth", vol: 0.15, delay: 0.04 });
        break;
      case "correct":
        this._beep({ freq: 660, slideTo: 990, dur: 0.18, type: "triangle", vol: 0.22 });
        break;
      case "wrong":
        this._beep({ freq: 220, slideTo: 110, dur: 0.28, type: "sawtooth", vol: 0.20 });
        break;
      case "final":
        // Mini victory jingle.
        const notes = [523, 659, 784, 1046];
        notes.forEach((f, i) => this._beep({ freq: f, dur: 0.22, type: "triangle", vol: 0.24, delay: i * 0.16 }));
        this._beep({ freq: 1568, dur: 0.4, type: "triangle", vol: 0.22, delay: 0.72 });
        break;
      case "join":
        this._beep({ freq: 880, dur: 0.08, type: "sine", vol: 0.16 });
        this._beep({ freq: 1320, dur: 0.10, delay: 0.09, type: "sine", vol: 0.16 });
        break;
      default:
        break;
    }
  }
}

export const maestroSfx = new SfxEngine();

export function useMaestroSfx() {
  const [muted, setMuted] = useState(maestroSfx.muted);
  useEffect(() => { setMuted(maestroSfx.muted); }, []);
  return useMemo(() => ({
    muted,
    toggleMute: () => { const m = maestroSfx.toggleMute(); setMuted(m); return m; },
    play: (n) => maestroSfx.play(n),
  }), [muted]);
}
